import { useCallback, useRef, useState } from 'react';

import { ConfirmDialogService } from '../../../AppConfirmDialog';
import { ASYNC_FLOW_CANCELLED } from '../../../shared/utils/asyncFlow';

import { MqrConsentIframeDialogProps } from './MqrConsentIframeDialog';
import { BankService as Service } from './BankService';
import {
  BankFormValue,
  isBankRequireConsent,
  mapBankFormToPayload,
  MqrConsentResponse,
  PaymentBankConfig,
} from './constants';

type PendingSave = {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
  executeSave: () => Promise<unknown>;
  formValue: BankFormValue;
};

const rejectWithMessage = (message: string) =>
  Promise.reject({
    response: {
      data: { message },
    },
  });

const MQR_CONSENT_CONFIRM_MESSAGE =
  'Tài khoản ngân hàng thụ hưởng của bạn cần đồng ý chia sẻ biến động số dư trước khi lưu. Bạn có muốn tiếp tục không?';

const isDefaultAccountChecked = (formValue: BankFormValue) =>
  Boolean(formValue.isDefault);

const isBankConsentRequired = (
  formValue: BankFormValue,
  paymentBankConfigs: PaymentBankConfig[],
) =>
  isBankRequireConsent(
    paymentBankConfigs,
    formValue.paymentProvider,
    formValue.code,
  );

/** Chỉ chạy luồng MQR khi tick tài khoản thụ hưởng chính + NH yêu cầu consent. */
const shouldRunMqrConsentFlow = (
  formValue: BankFormValue,
  paymentBankConfigs: PaymentBankConfig[],
) =>
  isDefaultAccountChecked(formValue) &&
  isBankConsentRequired(formValue, paymentBankConfigs);

/** Chỉ mở confirm + iframe khi đủ điều kiện nghiệp vụ và API báo chưa consent. */
const shouldOpenConsentConfirmAndIframe = (
  formValue: BankFormValue,
  paymentBankConfigs: PaymentBankConfig[],
  consent: MqrConsentResponse,
) =>
  shouldRunMqrConsentFlow(formValue, paymentBankConfigs) &&
  !consent.isError &&
  !consent.isConsentApproved &&
  Boolean(consent.iframeUrl);

const getMqrConsentParams = (formValue: BankFormValue) => ({
  bankAccount: formValue.bankAccount ?? '',
  bankCode: formValue.code ?? '',
});

const resolveConsentForSave = (
  consent: MqrConsentResponse,
  executeSave: () => Promise<unknown>,
) => {
  if (consent.isError) {
    return rejectWithMessage(
      consent.errorMessage ?? 'Kiểm tra consent thất bại',
    );
  }

  if (consent.isConsentApproved) {
    return executeSave();
  }

  return null;
};

const showConsentConfirmBeforeIframe = (
  iframeUrl: string,
  pending: PendingSave,
  openIframeConsent: (url: string, pending: PendingSave) => void,
) => {
  ConfirmDialogService.setProps({
    message: MQR_CONSENT_CONFIRM_MESSAGE,
    header: 'Xác nhận đồng ý điều khoản',
    icon: 'pi pi-shield',
    acceptLabel: 'Tiếp tục',
    rejectLabel: 'Hủy',
    accept: () => {
      openIframeConsent(iframeUrl, pending);
    },
    reject: () => {
      pending.reject(ASYNC_FLOW_CANCELLED);
    },
  });
};

export const useBankSaveWithConsent = (
  paymentBankConfigs: PaymentBankConfig[],
) => {
  const pendingSaveRef = useRef<PendingSave | null>(null);
  const isRecheckingRef = useRef(false);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [iframeVisible, setIframeVisible] = useState(false);
  const [recheckingConsent, setRecheckingConsent] = useState(false);

  const clearIframeDialog = useCallback(() => {
    setIframeVisible(false);
    setIframeUrl(null);
  }, []);

  const openIframeConsent = useCallback((url: string, pending: PendingSave) => {
    pendingSaveRef.current = pending;
    setIframeUrl(url);
    setIframeVisible(true);
  }, []);

  const recheckConsentAfterClose = useCallback(async () => {
    const pending = pendingSaveRef.current;
    if (!pending || isRecheckingRef.current) {
      return;
    }

    isRecheckingRef.current = true;
    setRecheckingConsent(true);

    try {
      if (
        !shouldRunMqrConsentFlow(pending.formValue, paymentBankConfigs)
      ) {
        const result = await pending.executeSave();
        pendingSaveRef.current = null;
        clearIframeDialog();
        pending.resolve(result);
        return;
      }

      const consent = await Service.getMqrConsent(
        getMqrConsentParams(pending.formValue),
      );
      const saveResult = resolveConsentForSave(consent, pending.executeSave);

      if (saveResult) {
        const result = await saveResult;
        pendingSaveRef.current = null;
        clearIframeDialog();
        pending.resolve(result);
        return;
      }

      pendingSaveRef.current = null;
      clearIframeDialog();
      pending.reject(ASYNC_FLOW_CANCELLED);
    } catch (error) {
      pendingSaveRef.current = null;
      clearIframeDialog();
      pending.reject(error);
    } finally {
      isRecheckingRef.current = false;
      setRecheckingConsent(false);
    }
  }, [clearIframeDialog, paymentBankConfigs]);

  const handleIframeDialogHide = useCallback(() => {
    if (recheckingConsent || isRecheckingRef.current) {
      return;
    }

    void recheckConsentAfterClose();
  }, [recheckConsentAfterClose, recheckingConsent]);

  const saveBankWithMqrConsent = useCallback(
    (formValue: BankFormValue, executeSave: () => Promise<unknown>) => {
      if (!shouldRunMqrConsentFlow(formValue, paymentBankConfigs)) {
        return executeSave();
      }

      return Service.getMqrConsent(getMqrConsentParams(formValue)).then(
        (consent) => {
          const saveResult = resolveConsentForSave(consent, executeSave);
          if (saveResult) {
            return saveResult;
          }

          if (
            shouldOpenConsentConfirmAndIframe(
              formValue,
              paymentBankConfigs,
              consent,
            )
          ) {
            return new Promise((resolve, reject) => {
              const pending: PendingSave = {
                resolve,
                reject,
                executeSave,
                formValue,
              };

              showConsentConfirmBeforeIframe(
                consent.iframeUrl!,
                pending,
                openIframeConsent,
              );
            });
          }

          return rejectWithMessage(
            consent.errorMessage ?? 'Chưa có liên kết consent',
          );
        },
      );
    },
    [openIframeConsent, paymentBankConfigs],
  );

  const createItemWithConsent = useCallback(
    (item: BankFormValue) =>
      saveBankWithMqrConsent(item, () =>
        Service.create(mapBankFormToPayload(item)),
      ),
    [saveBankWithMqrConsent],
  );

  const updateItemWithConsent = useCallback(
    (id: string, item: BankFormValue) =>
      saveBankWithMqrConsent(item, () =>
        Service.update(id, mapBankFormToPayload(item)),
      ),
    [saveBankWithMqrConsent],
  );

  const consentIframeDialog: MqrConsentIframeDialogProps = {
    visible: iframeVisible,
    iframeUrl,
    loading: recheckingConsent,
    onHide: handleIframeDialogHide,
  };

  return {
    createItemWithConsent,
    updateItemWithConsent,
    consentIframeDialog,
  };
};
